import spaceBackgroundLarge from 'assets/space/space-background-large.jpg';
import spaceBackgroundPlaceholder from 'assets/space/space-background-placeholder.jpg';
import spaceBackground from 'assets/space/space-background.jpg';
import spaceBoardLarge from 'assets/space/space-board-large.png';
import spaceBoardPlaceholder from 'assets/space/space-board-placeholder.png';
import spaceBoard from 'assets/space/space-board.png';
import { Button } from 'components/Button';
import { CodeBlock } from 'components/CodeBlock';
import { Footer } from 'components/Footer';
import { Image } from 'components/Image';
import { Meta } from 'components/Meta';
import { SpecTable } from 'components/SpecTable';
import { useTheme } from 'components/ThemeProvider';
import {
  ProjectBackground,
  ProjectContainer,
  ProjectHeader,
  ProjectSection,
  ProjectSectionColumns,
  ProjectSectionContent,
  ProjectSectionHeading,
  ProjectSectionText,
  ProjectTextRow,
} from 'layouts/Project';
import { Fragment } from 'react';
import { media } from 'utils/style';
import styles from './space.module.css';

const title = 'space';
const description =
  'space puts worktrees from several repos in one disposable workspace. Branch them together, ' +
  'send agents to work on the feature, then tear down the workspace without touching the source repos.';
const roles = ['Rust', 'clap + ratatui', 'Developer tooling'];

const composeSession = `$ space ws new checkout api web payments
  set      checkout (created from api, web, payments)
  worktree checkout-workspace-1/api      → branch checkout-workspace-1
  worktree checkout-workspace-1/web      → branch checkout-workspace-1
  worktree checkout-workspace-1/payments → branch checkout-workspace-1

$ space ws ls
  checkout-workspace-1   api web payments   checkout-workspace-1`;

const areaTree = `~/dev/acme/                      # an AREA — marked by a .space file
├── .space                       # marker + config (where the repos come from)
├── repos/                       # read-only clones — the primitives
│   ├── api/
│   └── web/
├── sets/                        # recipes: a manifest + docs, no code
│   └── checkout/
└── workspaces/                  # git worktrees — the only place you edit
    └── checkout-workspace-1/
        ├── api/                 # worktree of repos/api (nested, gitignored)
        └── web/`;

const forkSession = `$ space ws fork checkout-workspace-1 try-b
  re-instantiated sets/checkout → checkout-workspace-2
  branch try-b on api, web, payments`;

const growSession = `$ space ws add checkout-workspace-1 search
  worktree checkout-workspace-1/search → branch checkout-workspace-1

$ space set add checkout search
  recipe sets/checkout now lists search`;

const commandRows = [
  { label: 'area', value: 'init · add · root · ls · info · sync · prune · open · ide · rm' },
  { label: 'repo', value: 'ls · sync · add <url> · open · ide' },
  { label: 'set', value: 'new <name> <repo>… · ls · rm · sync · add <url> · open · ide' },
  { label: 'ws', value: 'new · ls · add · fork · branch · open · ide · rm' },
  { label: 'root', value: 'ui · completion <bash|zsh|fish> · bare space → TUI' },
];

const buildRows = [
  { label: 'Language', value: 'Rust, with clap for the CLI tree and ratatui for the TUI' },
  { label: 'Size', value: '~7.5k lines' },
  {
    label: 'Architecture',
    value: 'Hexagonal architecture: a pure core uses Git / Fs / Proc port traits; the CLI and TUI inject the real adapters',
  },
  {
    label: 'Optional companions',
    value: ['tmux: open drops you into a session', 'ghorg: mirrors an org into repos/', 'workmux: finds worktrees for agents', 'VS Code / Cursor: multi-root workspace files'],
  },
  { label: 'Hard dependency', value: 'git, and nothing else' },
];

export function Space() {
  const { themeId } = useTheme();
  const isDark = themeId === 'dark';

  return (
    <Fragment>
      <Meta title={title} prefix="Projects" description={description} />
      <ProjectContainer>
        <ProjectBackground
          srcSet={[spaceBackground, spaceBackgroundLarge]}
          placeholder={spaceBackgroundPlaceholder}
          opacity={isDark ? 0.5 : 0.8}
        />
        <ProjectHeader
          title={title}
          description={description}
          linkLabel="Private repo"
          roles={roles}
        />
        <ProjectSection padding="top">
          <ProjectSectionContent>
            <CodeBlock
              className={styles.banner}
              variant="terminal"
              title="fish: compose a working set"
            >
              {composeSession}
            </CodeBlock>
            <ProjectTextRow>
              <ProjectSectionHeading>One ticket across a dozen services</ProjectSectionHeading>
              <ProjectSectionText>
                A feature across a dozen services used to need a dozen clones, each with
                its own branch to keep in step. With space, you name the repos once. It creates
                a git worktree for each on a shared branch inside one disposable workspace.
              </ProjectSectionText>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection light={isDark}>
          <ProjectSectionContent>
            <ProjectTextRow>
              <ProjectSectionHeading>Three tiers, one invariant</ProjectSectionHeading>
              <ProjectSectionText>
                An area has read-only repo mirrors, sets that name which repos belong
                together, and workspaces for editing. Each workspace contains worktrees
                cut from the mirrors, so the clones never diverge. Deleting a workspace
                leaves the source repos untouched.
              </ProjectSectionText>
            </ProjectTextRow>
            <CodeBlock lang="text" className={styles.tree}>
              {areaTree}
            </CodeBlock>
          </ProjectSectionContent>
        </ProjectSection>
          <ProjectSection>
            <ProjectSectionColumns>
              <div className={styles.column}>
                <ProjectSectionHeading>Forking a workspace</ProjectSectionHeading>
                <ProjectSectionText>
                  A second instance of a set uses the mirrors and fresh branches, so two
                  agents can work on the same repos side by side. Copying a worktree aliases
                  its git metadata and corrupts state. The core therefore exposes no copy
                  or move operation.
                </ProjectSectionText>
                <CodeBlock variant="terminal" title="fish: a parallel instance">
                  {forkSession}
                </CodeBlock>
              </div>
              <div className={styles.column}>
                <ProjectSectionHeading>Grow the task or the recipe</ProjectSectionHeading>
                <ProjectSectionText>
                  Adding a repo to a workspace changes that instance. Adding it to the set
                  changes the recipe, so new workspaces from that set include the repo.
                  Checks before creation prevent a half-built workspace and report which
                  branch is already in use.
                </ProjectSectionText>
                <CodeBlock variant="terminal" title="fish: instance vs recipe">
                  {growSession}
                </CodeBlock>
              </div>
            </ProjectSectionColumns>
          </ProjectSection>
        <ProjectSection>
          <ProjectSectionContent>
            <ProjectTextRow>
              <ProjectSectionHeading>The cockpit</ProjectSectionHeading>
              <ProjectSectionText>
                <span className={styles.mono}>space serve</span> puts six screens over
                the same read-model the CLI prints. Attention shows which agents have
                stopped and are waiting on me, the board shows every workspace grouped by
                area and set, and the rest cover the journal feed, the task graph and the
                plans agents submit for approval. It is server-rendered Rust with one
                stylesheet and no JavaScript framework, and it only listens on localhost
                unless given a token.
              </ProjectSectionText>
              <Image
                raised
                srcSet={[spaceBoard, spaceBoardLarge]}
                placeholder={spaceBoardPlaceholder}
                alt="The space dashboard's board: two areas, org-acme and org-labs, each with its repo, set and workspace counts, and a card per workspace showing its set, member repos with their branches, and whether an agent session is running, stalled or parked."
                sizes={`(max-width: ${media.mobile}px) 100vw, 920px`}
              />
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection>
          <ProjectSectionContent>
            <ProjectTextRow>
              <ProjectSectionHeading>The surface</ProjectSectionHeading>
              <ProjectSectionText>
                Commands follow <span className={styles.mono}>space &lt;noun&gt; &lt;verb&gt;</span>.
                Completions use your actual areas, repos, sets and workspaces. Areas
                register themselves, so <span className={styles.mono}>-A &lt;area&gt;</span>{' '}
                works from any directory. A bare{' '}
                <span className={styles.mono}>space</span> opens the TUI browser.
              </ProjectSectionText>
            </ProjectTextRow>
            <SpecTable heading="Commands" rows={commandRows} />
            <SpecTable heading="Build" rows={buildRows} />
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection>
          <ProjectSectionContent>
            <ProjectTextRow center centerMobile noMargin>
              <ProjectSectionHeading>Where it is now</ProjectSectionHeading>
              <ProjectSectionText>
                I use space on my own machine every day. The repository is private for now.
                This is a short account; the design notes cover the port traits, worktree
                invariants and how the CLI, TUI and dashboard share one core.
              </ProjectSectionText>
              <Button
                secondary
                iconHoverShift
                icon="chevronRight"
                href="https://github.com/txmxthy"
              >
                More of my work on GitHub
              </Button>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
      </ProjectContainer>
      <Footer />
    </Fragment>
  );
}
